import type { DashStyleValue, SeriesOptionsType } from 'highcharts';
import { match, P } from 'ts-pattern';
import { localizeCurrencyValue } from 'utils/i18nHelpers';
import { colors } from 'ui/theme';
import {
  DeltaType,
  DELTA_TYPE_TO_COLOR_MAP,
} from 'modules/missionControl/lib/components/MetricsDetails';
import { localizePercent } from 'utils/l10n/percent';
import { fonts, fontSizes } from 'ui/theme';

export const parseMetricsForGraph = (name: string, metrics: any): SeriesOptionsType => {
  if (metrics) {
    const data = metrics?.histogram?.buckets?.map(({ time, value }: any) => [time, value]) || [];
    return { name, data, type: 'line' };
  }
  return { type: 'line' };
};

export const getGoalChartOptions = () => ({
  chart: {
    type: 'line',
    backgroundColor: 'transparent',
    height: 113,
    style: {
      fontFamily: fonts.sansSerif,
      fontSize: fontSizes.s14,
    },
  },
  colors: [DELTA_TYPE_TO_COLOR_MAP[DeltaType.Neutral].chartColor],
  xAxis: {
    // Show x axis crosshair on hover
    crosshair: {
      color: colors.borderStrong,
      dashStyle: 'Dash' as DashStyleValue,
      width: 1,
    },
    // Hide x axis labels and lines
    labels: {
      enabled: false,
    },
    tickColor: 'transparent',
    lineColor: 'transparent',
  },
  yAxis: {
    // Hide y axis labels and lines
    labels: {
      enabled: false,
    },
    gridLineColor: 'transparent',
    title: {
      text: undefined,
    },
  },
  tooltip: {
    useHTML: true,
    headerFormat: '',

    pointFormatter: function (): string {
      const {
        series: { name = '' } = {},
        y,
        x,
      } = this as any;

      const date = new Date(x).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
      });
      return `
        <div style="display: flex; gap: 8px; padding: 0 6px;
        ">
          <b>${y}</b>
          <span>${name}</span>
          <span style="color: ${colors.textOnColorSecondary}">${date}</span>
        </div>`;
    },
    backgroundColor: colors.backgroundDarkOverlay,
    borderColor: 'transparent',
    borderRadius: 6,
    padding: 6,
    style: {
      color: colors.white,
    },
    shadow: {
      color: '#000000',
      offsetX: 2.5,
      offsetY: 2.5,
      opacity: 0.015,
      width: 16,
    },
  },
  plotOptions: {
    series: {
      // Hide dot markers on graph
      marker: {
        enabled: false,
      },
      states: {
        // Remove hover effect on series line
        hover: {
          lineWidthPlus: 0,
        },
      },
    },
  },
  legend: {
    // Hide legend below graph
    enabled: false,
  },
});

export const formatAssociatedMetricValue = (
  associatedMetricLabel: string,
  associatedMetricValue: number | string,
) => {
  return match(associatedMetricLabel?.toLowerCase())
    .with(P.string.includes('rate'), () => localizePercent(Number(associatedMetricValue)))
    .with(P.string.includes('revenue'), () => localizeCurrencyValue(Number(associatedMetricValue)))
    .otherwise(() => associatedMetricValue?.toLocaleString());
};
